import { toErrorMessage } from "@src/shared/utils/errors";
import type { Outcome } from "@src/shared/utils/observability/events";

export interface CopyNoteHandlerOptions {
  hasSoapBody: boolean;
  soapnoteText: string;
  encounterNumeric: number | undefined;
  patientIdNum: number | null;
  clipboard: Pick<Clipboard, "writeText">;
  track: (
    payload: { encounter_id: number; patient_id: number } & Outcome,
  ) => void;
  toastError: (message: string) => void;
}

/**
 * Pure handler for the "Copy SOAP note" action.
 *
 * The clipboard write is the user-facing action and must not be gated on
 * analytics metadata. Analytics IDs (`encounterNumeric`, `patientIdNum`) are
 * only required to fire the tracking event — if either is absent the copy
 * still succeeds silently.
 */
export async function copyNoteHandler({
  hasSoapBody,
  soapnoteText,
  encounterNumeric,
  patientIdNum,
  clipboard,
  track,
  toastError,
}: CopyNoteHandlerOptions): Promise<void> {
  if (!hasSoapBody) return;
  try {
    await clipboard.writeText(soapnoteText);
    if (encounterNumeric != null && patientIdNum != null) {
      track({
        encounter_id: encounterNumeric,
        patient_id: patientIdNum,
        success: true,
      });
    }
  } catch (err) {
    if (encounterNumeric != null && patientIdNum != null) {
      track({
        encounter_id: encounterNumeric,
        patient_id: patientIdNum,
        success: false,
        error: toErrorMessage(err),
      });
    }
    toastError("Unable to copy to clipboard. Check browser permissions.");
  }
}
